import type { ChatPart } from 'features/timeSeries/agentTab/chatStream'

/**
 * Attach the agent's reasoning to the tool call it explains.
 *
 * The agent narrates as it works — it reads a result, says what it made of it,
 * then calls the next tool. Rendered as separate blocks that reads as
 * disconnected: a floating paragraph, then a line saying "Searching tags for
 * 'HT water'", with nothing tying the second to the first. Yet the paragraph IS
 * the reason for the call.
 *
 * So a short piece of text immediately followed by a tool call becomes that
 * call's caption. Long text stays a paragraph of its own, because a real
 * explanation — the stage-2 report, the finding at the end — is not a caption
 * and should not be squeezed into one.
 */

/** Above this, the text is prose in its own right rather than a lead-in. */
export const CAPTION_LIMIT = 240

export interface CaptionedPart {
  part: ChatPart
  /** The agent's own words for why this call is happening. */
  caption?: string
}

export const captionParts = (parts: ChatPart[]): CaptionedPart[] => {
  const out: CaptionedPart[] = []
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i]
    const next = parts[i + 1]
    if (
      part.kind === 'text' &&
      next?.kind === 'tool' &&
      part.text.trim().length > 0 &&
      part.text.trim().length <= CAPTION_LIMIT &&
      // A heading or a list is structure, not a sentence of intent.
      !/[\n#*|]/.test(part.text.trim())
    ) {
      out.push({ part: next, caption: part.text.trim() })
      i += 1
      continue
    }
    out.push({ part })
  }
  return out
}
